'use client';

import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';

import { CompanyPersonSelectFields } from '@/features/contacts/components';
import { JobDeliverableSelectFields } from '@/features/jobs/components';
import { ServiceTypeSelect } from '@/features/service-types/components';
import { useCreateTaskMutation } from '@/features/tasks';
import { TaskStatusSelect } from '@/features/tasks/components';
import { AssigneeSelect } from '@/features/workspaces/components';
import {
  ConfirmActionModal,
  DatePicker,
  Divider,
  Drawer,
  MainButton,
  SecondaryButton,
  SectionTabs,
  SmartInput,
} from '@web/ui';
import {
  backendErrorsToFieldErrors,
  getErrorMessage,
  notify,
} from '@web/utils';

import styles from './TaskCreateDrawer.module.scss';

import type { CompanyOption } from '@/features/contacts/companies/components/CompanySelectField';
import type { PersonOption } from '@/features/contacts/people/components/PersonSelectField';
import type { CreateTaskPayload, TaskStatus, TaskType } from '@/features/tasks';
import type { Dayjs } from 'dayjs';

type TaskCreateDrawerPrefill = {
  title?: string | null;
  description?: string | null;
};

type TaskCreateDrawerSourceItem = {
  id: string;
  title?: string | null;
  description?: string | null;
};

type TaskCreateDrawerProps = {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  prefill?: TaskCreateDrawerPrefill;
  sourceItem?: TaskCreateDrawerSourceItem | null;
  onDeleteSourceItem?: (sourceItemId: string) => Promise<void>;
  onCreated?: () => void;
};

type ClientMode = 'client' | 'internal';

type TaskCreateFormState = {
  taskType: TaskType;
  title: string;
  description: string;
  status: TaskStatus;
  serviceTypeId: string;
  levelOfEffort: number | null;
  dueDate: Dayjs | null;
  assigneeId: string;
  clientMode: ClientMode;
  company: CompanyOption | null;
  person: PersonOption | null;
  jobId: string;
  deliverableId: string;
  deleteSourceItemAfterCreate: boolean;
};

type TaskCreateFormErrors = Partial<
  Record<
    | 'title'
    | 'description'
    | 'status'
    | 'service_type_id'
    | 'level_of_effort'
    | 'due_date'
    | 'assignee_id'
    | 'company_id'
    | 'person_id'
    | 'job_id'
    | 'deliverable_id'
    | 'form',
    string
  >
>;

const TASK_TYPE_SECTIONS = [
  { id: 'quick', label: 'Quick Task' },
  { id: 'job', label: 'Job Task' },
] as const;

const CLIENT_MODE_SECTIONS = [
  { id: 'client', label: 'Client' },
  { id: 'internal', label: 'Internal' },
] as const;

function toIsoDateString(rawValue: Dayjs | null): string | null {
  if (!rawValue || !rawValue.isValid()) {
    return null;
  }

  return rawValue.startOf('day').toISOString();
}

function getInitialFormState({
  prefill,
  sourceItem,
}: {
  prefill?: TaskCreateDrawerPrefill;
  sourceItem?: TaskCreateDrawerSourceItem | null;
}): TaskCreateFormState {
  const initialTitle = prefill?.title ?? sourceItem?.title ?? '';
  const initialDescription =
    prefill?.description ?? sourceItem?.description ?? '';

  return {
    taskType: 'quick',
    title: initialTitle,
    description: initialDescription,
    status: 'inprogress',
    serviceTypeId: '',
    levelOfEffort: null,
    dueDate: null,
    assigneeId: '',
    clientMode: 'client',
    company: null,
    person: null,
    jobId: '',
    deliverableId: '',
    deleteSourceItemAfterCreate: Boolean(sourceItem),
  };
}

function validateTaskForm(values: TaskCreateFormState): TaskCreateFormErrors {
  const nextErrors: TaskCreateFormErrors = {};

  if (!values.title.trim()) {
    nextErrors.title = 'Title is required.';
  }

  if (!values.serviceTypeId) {
    nextErrors.service_type_id = 'Service type is required.';
  }

  if (values.levelOfEffort === null || values.levelOfEffort < 0) {
    nextErrors.level_of_effort = 'LOE (hours) is required.';
  }

  if (!values.dueDate) {
    nextErrors.due_date = 'Due date is required.';
  } else if (!values.dueDate.isValid()) {
    nextErrors.due_date = 'Use a valid date (YYYY-MM-DD).';
  }

  if (!values.assigneeId) {
    nextErrors.assignee_id = 'Assignee is required.';
  }

  if (values.taskType === 'job') {
    if (!values.jobId) {
      nextErrors.job_id = 'Job is required for job tasks.';
    }
    if (!values.deliverableId) {
      nextErrors.deliverable_id = 'Deliverable is required for job tasks.';
    }
  }

  return nextErrors;
}

function hasChanges(
  current: TaskCreateFormState,
  initial: TaskCreateFormState,
): boolean {
  const currentDueDate = current.dueDate?.startOf('day').valueOf() ?? null;
  const initialDueDate = initial.dueDate?.startOf('day').valueOf() ?? null;

  return (
    current.taskType !== initial.taskType ||
    current.title !== initial.title ||
    current.description !== initial.description ||
    current.status !== initial.status ||
    current.serviceTypeId !== initial.serviceTypeId ||
    current.levelOfEffort !== initial.levelOfEffort ||
    currentDueDate !== initialDueDate ||
    current.assigneeId !== initial.assigneeId ||
    current.clientMode !== initial.clientMode ||
    (current.company?.id ?? null) !== (initial.company?.id ?? null) ||
    (current.person?.id ?? null) !== (initial.person?.id ?? null) ||
    current.jobId !== initial.jobId ||
    current.deliverableId !== initial.deliverableId ||
    current.deleteSourceItemAfterCreate !== initial.deleteSourceItemAfterCreate
  );
}

function mapBackendTaskFieldPath(path: string): string | null {
  if (path === 'title.0' || path === 'title') return 'title';
  if (path === 'description.0' || path === 'description') return 'description';
  if (path === 'status.0' || path === 'status') return 'status';
  if (path === 'due_date.0' || path === 'due_date') return 'due_date';
  if (path === 'job_id.0' || path === 'job_id') return 'job_id';
  if (path === 'deliverable_id.0' || path === 'deliverable_id')
    return 'deliverable_id';
  if (path === 'company_contact_id.0' || path === 'company_contact_id')
    return 'company_id';
  if (path === 'person_contact_id.0' || path === 'person_contact_id')
    return 'person_id';
  if (path === 'task_assignee_payloads.0.user_id') return 'assignee_id';
  if (path === 'task_assignee_payloads.0.service_type_id')
    return 'service_type_id';
  if (path === 'task_assignee_payloads.0.level_of_effort')
    return 'level_of_effort';
  if (path === 'non_field_errors.0') return 'form';

  return path;
}

export function TaskCreateDrawer({
  open,
  onClose,
  onExited,
  prefill,
  sourceItem = null,
  onDeleteSourceItem,
  onCreated,
}: TaskCreateDrawerProps) {
  const [initialFormState] = useState<TaskCreateFormState>(() =>
    getInitialFormState({ prefill, sourceItem }),
  );
  const [form, setForm] = useState<TaskCreateFormState>(initialFormState);
  const [formErrors, setFormErrors] = useState<TaskCreateFormErrors>({});
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [createTask, { isLoading: isCreatingTask }] = useCreateTaskMutation();
  const hasUnsavedChanges = useMemo(
    () => hasChanges(form, initialFormState),
    [form, initialFormState],
  );
  const titleText =
    form.taskType === 'quick' ? 'New Quick Task' : 'New Job Task';

  const clearFieldError = (field: keyof TaskCreateFormErrors) => {
    setFormErrors((currentErrors) => {
      if (!currentErrors[field]) {
        return currentErrors;
      }

      return {
        ...currentErrors,
        [field]: undefined,
      };
    });
  };

  const handleAttemptClose = () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }

    setShowDiscardModal(true);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationErrors = validateTaskForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    const dueDateIso = toIsoDateString(form.dueDate);
    if (!dueDateIso) {
      setFormErrors((currentErrors) => ({
        ...currentErrors,
        due_date: 'Use a valid date (YYYY-MM-DD).',
      }));
      return;
    }

    setFormErrors({});

    const payload: CreateTaskPayload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      status: form.status,
      due_date: dueDateIso,
      company_contact_id:
        form.clientMode === 'client' ? (form.company?.id ?? null) : null,
      person_contact_id:
        form.clientMode === 'client' ? (form.person?.id ?? null) : null,
      task_assignee_payloads: [
        {
          user_id: form.assigneeId,
          service_type_id: form.serviceTypeId,
          level_of_effort: form.levelOfEffort ?? 0,
        },
      ],
    };

    if (form.taskType === 'job') {
      payload.job_id = form.jobId;
      payload.deliverable_id = form.deliverableId;
    }

    try {
      await createTask(payload).unwrap();

      if (
        form.deleteSourceItemAfterCreate &&
        sourceItem &&
        onDeleteSourceItem
      ) {
        try {
          await onDeleteSourceItem(sourceItem.id);
        } catch (deleteError) {
          notify({ type: 'error', message: getErrorMessage(deleteError) });
        }
      }

      notify({ type: 'success', message: 'Task created successfully.' });
      onCreated?.();
      onClose();
    } catch (error) {
      const backendErrors = (error as any)?.data?.errors;
      const mappedErrors = backendErrorsToFieldErrors(backendErrors, {
        mapPath: mapBackendTaskFieldPath,
      });

      if (Object.keys(mappedErrors).length > 0) {
        setFormErrors(mappedErrors as TaskCreateFormErrors);
      }

      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleAttemptClose}
        onExited={onExited}
        anchor="right"
        width={400}
      >
        <form className={styles.taskCreateDrawer} onSubmit={handleSubmit}>
          <header className={styles.taskCreateDrawer__header}>
            <div className={styles.taskCreateDrawer__headerCopy}>
              <span className={styles.taskCreateDrawer__eyebrow}>
                Create Task
              </span>
              <h2 className={styles.taskCreateDrawer__title}>{titleText}</h2>
            </div>

            <button
              type="button"
              aria-label="Close task drawer"
              className={styles.taskCreateDrawer__closeButton}
              onClick={handleAttemptClose}
            >
              <FiX />
            </button>
          </header>

          <div className={styles.taskCreateDrawer__body}>
            <SectionTabs
              sections={TASK_TYPE_SECTIONS.map((section) => ({
                id: section.id,
                label: section.label,
              }))}
              activeId={form.taskType}
              onSectionChange={(nextTaskType) => {
                setForm((current) => ({
                  ...current,
                  taskType: nextTaskType as TaskType,
                }));
              }}
              aria-label="Task type"
            />

            <section className={styles.taskCreateDrawer__section}>
              <p className={styles.taskCreateDrawer__sectionTitle}>Basics</p>
              <SmartInput
                type="text"
                name="task-title"
                label="Title"
                value={form.title}
                error={formErrors.title}
                onChange={(nextValue) => {
                  setForm((current) => ({ ...current, title: nextValue }));
                  clearFieldError('title');
                }}
              />
              <SmartInput
                type="textarea"
                name="task-description"
                label="Description"
                value={form.description}
                error={formErrors.description}
                onChange={(nextValue) => {
                  setForm((current) => ({
                    ...current,
                    description: nextValue,
                  }));
                  clearFieldError('description');
                }}
              />
            </section>

            <Divider />

            <section className={styles.taskCreateDrawer__section}>
              <p className={styles.taskCreateDrawer__sectionTitle}>Planning</p>

              <div className={styles.taskCreateDrawer__row}>
                <ServiceTypeSelect
                  value={form.serviceTypeId}
                  onChange={(nextValue) => {
                    setForm((current) => ({
                      ...current,
                      serviceTypeId: nextValue,
                    }));
                    clearFieldError('service_type_id');
                  }}
                  name="task-service-type"
                  label="Service Type"
                  includeAllOption={false}
                  error={formErrors.service_type_id}
                  size="medium"
                />

                <SmartInput
                  type="number"
                  name="task-loe-hours"
                  label="LOE (hours)"
                  value={form.levelOfEffort}
                  min={0}
                  step={0.1}
                  error={formErrors.level_of_effort}
                  onChange={(nextValue) => {
                    setForm((current) => ({
                      ...current,
                      levelOfEffort: nextValue,
                    }));
                    clearFieldError('level_of_effort');
                  }}
                />
              </div>

              <div
                className={`${styles.taskCreateDrawer__row} ${styles.taskCreateDrawer__dateStatusRow}`}
              >
                <DatePicker
                  mode="single"
                  name="task-due-date"
                  label="Due date"
                  value={form.dueDate}
                  error={formErrors.due_date}
                  onChange={(nextValue) => {
                    setForm((current) => ({
                      ...current,
                      dueDate: nextValue,
                    }));
                    clearFieldError('due_date');
                  }}
                />

                <TaskStatusSelect
                  value={form.status}
                  error={formErrors.status}
                  onChange={(nextValue) => {
                    setForm((current) => ({
                      ...current,
                      status: nextValue,
                    }));
                    clearFieldError('status');
                  }}
                />
              </div>

              {form.taskType === 'job' ? (
                <JobDeliverableSelectFields
                  value={{
                    jobId: form.jobId,
                    deliverableId: form.deliverableId,
                  }}
                  layout="horizontal"
                  jobName="task-job-id"
                  deliverableName="task-deliverable-id"
                  jobError={formErrors.job_id}
                  deliverableError={formErrors.deliverable_id}
                  onChange={(nextSelection) => {
                    const jobChanged = nextSelection.jobId !== form.jobId;
                    const deliverableChanged =
                      nextSelection.deliverableId !== form.deliverableId;

                    setForm((current) => ({
                      ...current,
                      jobId: nextSelection.jobId,
                      deliverableId: nextSelection.deliverableId,
                    }));

                    if (jobChanged) {
                      clearFieldError('job_id');
                      clearFieldError('deliverable_id');
                      return;
                    }

                    if (deliverableChanged) {
                      clearFieldError('deliverable_id');
                    }
                  }}
                />
              ) : null}
            </section>

            <Divider />

            <section className={styles.taskCreateDrawer__section}>
              <p className={styles.taskCreateDrawer__sectionTitle}>Ownership</p>

              <div className={styles.taskCreateDrawer__row}>
                <AssigneeSelect
                  value={form.assigneeId}
                  error={formErrors.assignee_id}
                  onChange={(nextValue) => {
                    setForm((current) => ({
                      ...current,
                      assigneeId: nextValue,
                    }));
                    clearFieldError('assignee_id');
                  }}
                />
              </div>
            </section>

            <Divider />

            <section className={styles.taskCreateDrawer__section}>
              <p className={styles.taskCreateDrawer__sectionTitle}>
                Who is this for?
              </p>

              <SectionTabs
                sections={CLIENT_MODE_SECTIONS.map((section) => ({
                  id: section.id,
                  label: section.label,
                }))}
                activeId={form.clientMode}
                onSectionChange={(nextClientMode) => {
                  setForm((current) => ({
                    ...current,
                    clientMode: nextClientMode as ClientMode,
                  }));
                }}
                aria-label="Client mode"
              />

              {form.clientMode === 'client' ? (
                <div className={styles.taskCreateDrawer__rowSingle}>
                  <CompanyPersonSelectFields
                    company={form.company}
                    person={form.person}
                    companyLabel="Company"
                    personLabel="Person"
                    personPlaceholder="Search people..."
                    companyError={formErrors.company_id}
                    personError={formErrors.person_id}
                    layout="inline"
                    onCompanyChange={(nextCompany, nextPerson) => {
                      setForm((current) => ({
                        ...current,
                        company: nextCompany,
                        person: nextPerson,
                      }));
                      clearFieldError('company_id');
                      clearFieldError('person_id');
                    }}
                    onPersonChange={(nextPerson, nextCompany) => {
                      setForm((current) => ({
                        ...current,
                        company: nextCompany,
                        person: nextPerson,
                      }));
                      clearFieldError('company_id');
                      clearFieldError('person_id');
                    }}
                  />
                </div>
              ) : null}

              <Divider />

              {sourceItem && onDeleteSourceItem ? (
                <div className={styles.taskCreateDrawer__checkboxWrap}>
                  <SmartInput
                    type="checkbox"
                    name="task-delete-source-item"
                    label="Delete list item after creating task"
                    checked={form.deleteSourceItemAfterCreate}
                    onChange={(nextChecked) => {
                      setForm((current) => ({
                        ...current,
                        deleteSourceItemAfterCreate: nextChecked,
                      }));
                    }}
                  />
                </div>
              ) : null}
            </section>

            {formErrors.form ? (
              <p className={styles.taskCreateDrawer__error}>
                {formErrors.form}
              </p>
            ) : null}
          </div>

          <footer className={styles.taskCreateDrawer__footer}>
            <div className={styles.taskCreateDrawer__footerActions}>
              <SecondaryButton
                ariaLabel="Cancel task creation"
                onClick={handleAttemptClose}
              >
                Cancel
              </SecondaryButton>
              <MainButton
                type="submit"
                ariaLabel="Create task"
                isLoading={isCreatingTask}
              >
                Create
              </MainButton>
            </div>
          </footer>
        </form>
      </Drawer>

      <ConfirmActionModal
        open={showDiscardModal}
        title="Discard changes?"
        description="Your changes will be lost."
        confirmButtonLabel="Discard"
        cancelButtonLabel="Keep editing"
        confirmButtonVariant="primary"
        requireConfirmationText={false}
        onConfirm={async () => {
          setShowDiscardModal(false);
          onClose();
        }}
        onClose={() => setShowDiscardModal(false)}
      />
    </>
  );
}

export type { TaskCreateDrawerPrefill, TaskCreateDrawerSourceItem };
